/**
 * AdminWorkersAtWorkPage
 * ----------------------
 * Park-wise gallery of the workers-at-work proof photos that PMC
 * supervisors captured before each cleaning shift. Each card lists one
 * park and shows every supervisor's submission for the selected date
 * with their photos + GPS coordinates + capture timestamps.
 *
 * Data: GET /api/admin/workers-at-work?date=YYYY-MM-DD&park_id=...
 */
import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays,
  MapPin,
  Users,
  ImageIcon,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

function todayISO() {
  const d = new Date();
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
}

function fmtIST(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminWorkersAtWorkPage() {
  const [date, setDate] = useState(todayISO());
  const [parks, setParks] = useState([]);
  const [parkId, setParkId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  // load park master once
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/parks");
        setParks(data || []);
      } catch {
        // best effort — filter dropdown is optional
      }
    })();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = { date };
      if (parkId) params.park_id = parkId;
      const { data } = await api.get("/admin/workers-at-work", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || "Failed to load workers-at-work photos");
      toast.error(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // load on initial mount + when filters change
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, parkId]);

  const totalPhotos = useMemo(
    () => rows.reduce((acc, r) => acc + (r.total_photos || 0), 0),
    [rows],
  );
  const totalParks = rows.length;
  const totalSupervisors = useMemo(
    () => rows.reduce((acc, r) => acc + (r.supervisors?.length || 0), 0),
    [rows],
  );

  return (
    <AppShell
      title="Workers at Work"
      subtitle="Park-wise gallery of worker-at-work proof photos submitted with the Before-cleaning checklist."
      action={
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading}
          data-testid="waw-refresh"
        >
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      }
    >
      {/* Filters */}
      <Card className="mb-3">
        <CardContent className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Date</Label>
            <div className="relative">
              <CalendarDays className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="pl-8"
                data-testid="waw-date-input"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Park</Label>
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <select
                value={parkId}
                onChange={(e) => setParkId(e.target.value)}
                className="h-9 w-full appearance-none rounded-md border bg-background pl-8 pr-7 text-sm"
                data-testid="waw-park-select"
              >
                <option value="">All parks</option>
                {parks
                  .filter((p) => p.active !== false)
                  .sort((a, b) => (a.code || "").localeCompare(b.code || ""))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.name}
                    </option>
                  ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>
          <div className="flex items-end justify-end gap-2 text-xs">
            <Badge variant="outline" className="border-sky-300 bg-sky-50 text-sky-900" data-testid="waw-summary-parks">
              <MapPin className="mr-1 h-3 w-3" /> {totalParks} parks
            </Badge>
            <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-900" data-testid="waw-summary-sups">
              <Users className="mr-1 h-3 w-3" /> {totalSupervisors} submissions
            </Badge>
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900" data-testid="waw-summary-photos">
              <ImageIcon className="mr-1 h-3 w-3" /> {totalPhotos} photos
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Body */}
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="waw-empty">
            No workers-at-work photos submitted for {date}
            {parkId ? " in the selected park." : "."}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {rows.map((r) => (
            <Card key={r.park_id} data-testid={`waw-park-card-${r.park_code || r.park_id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="text-base text-slate-900">
                      <span className="font-mono text-xs text-slate-500">{r.park_code || "—"}</span>{" "}
                      {r.park_name || "—"}
                    </CardTitle>
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {r.supervisors.length} supervisor{r.supervisors.length === 1 ? "" : "s"} · {r.total_photos} photo{r.total_photos === 1 ? "" : "s"}
                    </div>
                  </div>
                  <Badge variant="outline" className="border-sky-300 bg-sky-50 text-sky-900">
                    {fmtIST(r.date + "T00:00:00+05:30").split(",")[0]}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-1">
                {r.supervisors.map((s, sidx) => (
                  <div
                    key={s.checklist_id || sidx}
                    className="rounded-md border border-slate-200 bg-slate-50/40 p-2"
                    data-testid={`waw-sup-${r.park_code}-${sidx}`}
                  >
                    <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs">
                      <span className="font-semibold text-slate-900">
                        {s.supervisor_name || "—"}
                      </span>
                      <Badge
                        variant="outline"
                        className={
                          s.visit_mode === "visited"
                            ? "border-emerald-300 bg-emerald-50 text-[10px] text-emerald-900"
                            : "border-amber-300 bg-amber-50 text-[10px] text-amber-900"
                        }
                      >
                        {s.visit_mode === "visited" ? "Visited" : "Remote"}
                      </Badge>
                      <span className="ml-auto text-[10px] text-muted-foreground">
                        {fmtIST(s.submitted_at)}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      {s.photos.map((p, pidx) => {
                        const src = p.photo_b64?.startsWith("data:")
                          ? p.photo_b64
                          : `data:image/jpeg;base64,${p.photo_b64 || ""}`;
                        const hasGps =
                          typeof p.gps_lat === "number" && typeof p.gps_lng === "number";
                        return (
                          <button
                            key={pidx}
                            type="button"
                            onClick={() => setLightbox({ src, photo: p })}
                            className="group relative aspect-square overflow-hidden rounded ring-1 ring-slate-200 hover:ring-sky-400"
                            data-testid={`waw-photo-${r.park_code}-${sidx}-${pidx}`}
                            title={hasGps ? `GPS ${p.gps_lat.toFixed(5)}, ${p.gps_lng.toFixed(5)}` : "No GPS"}
                          >
                            <img
                              src={src}
                              alt="worker"
                              className="h-full w-full object-cover transition-transform group-hover:scale-105"
                            />
                            <span
                              className={`absolute bottom-0 left-0 right-0 px-1 py-0.5 text-center text-[9px] font-medium ${
                                hasGps
                                  ? "bg-emerald-900/80 text-white"
                                  : "bg-rose-900/80 text-white"
                              }`}
                            >
                              {hasGps ? "GPS ✓" : "No GPS"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(null)}
          data-testid="waw-lightbox"
        >
          <div
            className="relative max-h-[90vh] max-w-3xl rounded-md bg-white p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.src}
              alt="worker enlarged"
              className="max-h-[80vh] max-w-full rounded object-contain"
            />
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-700">
              <span>
                Captured: <span className="font-medium">{fmtIST(lightbox.photo?.captured_at)}</span>
              </span>
              {typeof lightbox.photo?.gps_lat === "number" ? (
                <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-900">
                  <MapPin className="mr-1 h-3 w-3" />
                  {lightbox.photo.gps_lat.toFixed(5)}, {lightbox.photo.gps_lng.toFixed(5)}
                </Badge>
              ) : (
                <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-900">
                  No GPS
                </Badge>
              )}
              {lightbox.photo?.source ? (
                <Badge variant="outline">{lightbox.photo.source.replace("_", " ")}</Badge>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => setLightbox(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </AppShell>
  );
}
